import { saveAs } from "file-saver";
import { useMemo, useEffect, useState } from "react";
import { IoDocumentTextSharp, IoFilter } from "react-icons/io5";
import { LuLayoutGrid, LuCopy, LuCheck } from "react-icons/lu";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import WpDynamicTable from "../../Components/WhatsappTable/WpDynamicTable";
import { fetchEInvoiceEwayResponse } from "../../Redux/Einvoice&EwayBillSlice/Einvoice&EwayBill";
import Loader from "../../Components/Loader/Loader";

const ResponseCell = ({ text, limit = 50, isAllExpanded = false }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [copied, setCopied] = useState(false);
    const [isFormatted, setIsFormatted] = useState(false);

    const formattedJSON = useMemo(() => {
        if (!text || text === "-") return null;
        
        // 1. Try standard single JSON object/array parse
        try {
            const parsed = JSON.parse(text);
            if (parsed && typeof parsed === "object") {
                return JSON.stringify(parsed, null, 2);
            }
        } catch (e) {}

        // 2. Try single JSON parse with single-to-double quote substitution
        try {
            const normalized = text.replace(/'/g, '"');
            const parsed = JSON.parse(normalized);
            if (parsed && typeof parsed === "object") {
                return JSON.stringify(parsed, null, 2);
            }
        } catch (e) {}

        // 3. Try parsing concatenated JSON objects ({...}{...})
        try {
            const results = [];
            let braceCount = 0;
            let startIndex = -1;

            for (let i = 0; i < text.length; i++) {
                const char = text[i];
                if (char === '{') {
                    if (braceCount === 0) {
                        startIndex = i;
                    }
                    braceCount++;
                } else if (char === '}') {
                    braceCount--;
                    if (braceCount === 0 && startIndex !== -1) {
                        const jsonStr = text.substring(startIndex, i + 1);
                        try {
                            results.push(JSON.parse(jsonStr));
                        } catch (e1) {
                            try {
                                const normalized = jsonStr.replace(/'/g, '"');
                                results.push(JSON.parse(normalized));
                            } catch (e2) {}
                        }
                        startIndex = -1;
                    }
                }
            }

            if (results.length > 0) {
                if (results.length === 1) {
                    return JSON.stringify(results[0], null, 2);
                }
                return JSON.stringify(results, null, 2);
            }
        } catch (e) {}

        return null;
    }, [text]);

    if (!text || text === "-") return "-";

    const handleCopy = async (e) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(isFormatted && formattedJSON ? formattedJSON : text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy text: ", err);
        }
    };

    const activeText = isFormatted && formattedJSON ? formattedJSON : text;

    const charLimit = limit;
    const currentlyExpanded = isAllExpanded || isExpanded;
    const isTruncated = activeText.length > charLimit;
    const displayText = isTruncated ? (currentlyExpanded ? activeText : activeText.slice(0, charLimit) + "...") : activeText;

    return (
        <div className="text-left font-medium text-slate-700 leading-normal break-words relative group min-h-[24px] flex items-start pr-16 w-full py-1">
            <div className="w-full">
                {isFormatted && formattedJSON ? (
                    <pre className={`bg-slate-50 border border-slate-200 rounded p-2 text-xs font-mono text-slate-800 overflow-x-auto whitespace-pre-wrap break-all scrollbar-thin ${currentlyExpanded ? "max-h-none overflow-y-visible" : "max-h-[300px] overflow-y-auto"}`}>
                        {displayText}
                    </pre>
                ) : (
                    <div className={currentlyExpanded ? "whitespace-normal break-all text-slate-700 font-medium py-0.5" : "overflow-x-auto whitespace-nowrap scrollbar-thin max-w-full text-slate-700 font-medium py-0.5"}>
                        {displayText}
                    </div>
                )}
                {isTruncated && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsExpanded(!isExpanded);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 font-bold ml-1.5 focus:outline-none cursor-pointer border-none bg-transparent inline-flex items-center"
                    >
                        {currentlyExpanded ? "Read Less" : "Read More"}
                    </button>
                )}
            </div>
            
            <div className="absolute right-0 top-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                {formattedJSON && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsFormatted(!isFormatted);
                        }}
                        className={`px-1.5 py-1 rounded hover:bg-slate-100 transition-all text-[10px] font-extrabold border border-slate-300 bg-white cursor-pointer flex items-center justify-center shadow-sm ${
                            isFormatted ? "text-blue-600 border-blue-400 bg-blue-50" : "text-slate-500 hover:text-slate-700"
                        }`}
                        title={isFormatted ? "Show raw response" : "Format JSON response"}
                    >
                        JSON
                    </button>
                )}
                <button
                    onClick={handleCopy}
                    className="p-1.5 rounded hover:bg-slate-100 transition-all text-slate-400 hover:text-slate-700 focus:outline-none cursor-pointer border-none bg-transparent flex items-center justify-center"
                    title="Copy to clipboard"
                >
                    {copied ? (
                        <LuCheck className="text-green-600" size={15} />
                    ) : (
                        <LuCopy size={15} />
                    )}
                </button>
            </div>
        </div>
    );
};

const EInvoice_Eway_Response = () => {
    const formatDisplayDate = (dateStr) => {
        if (!dateStr || dateStr === "-") return "-";
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");
        return `${day}-${month}-${year} ${hours}:${minutes}`;
    };

    const dispatch = useDispatch();

    const { EInvoiceEwayResponseData, EInvoiceEwayResponseLoading } = useSelector((state) => state.EinvoiceEwayBill);

    const [loading, setLoading] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [expandAll, setExpandAll] = useState(false);

    const [filters, setFilters] = useState({
        ClientName: "",
        Type: "",
        DateFrom: "",
        DateTo: "",
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                await dispatch(fetchEInvoiceEwayResponse());
                setDataLoaded(true);
            } catch (error) {
                console.error("Error fetching E-Invoice/E-Way response data:", error);
                setDataLoaded(true);
            }
        };
        fetchData();
    }, [dispatch]);

    const filterdata = useMemo(() => {
        return (EInvoiceEwayResponseData || [])?.filter((item) => {
            const itemDate = new Date(item.WhaSendDatetime || item.createdAt || item.SendDate || item.DateTime);
            const dateTo = filters.DateTo ? new Date(filters.DateTo) : null;
            if (dateTo) {
                dateTo.setHours(23, 59, 59, 999);
            }

            const dateFrom = filters.DateFrom ? new Date(filters.DateFrom) : null;
            if (dateFrom) {
                dateFrom.setHours(0, 0, 0, 0);
            }

            return (
                (!filters.ClientName ||
                    item.ClientID?.WhaClientName === filters.ClientName) &&
                (!filters.Type || (filters.Type === "Einvoice" ? item.Types?.toString().includes("EINV") : filters.Type === "Eway" ? item.Types?.toString().includes("EWB") : filters.Type === "GSTINSEARCH" ? item.Types?.toString().includes("GSTIN") : true)) &&
                (!dateFrom || itemDate >= dateFrom) &&
                (!dateTo || itemDate <= dateTo)
            );
        });
    }, [EInvoiceEwayResponseData, filters]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters({
            ClientName: "",
            Type: "",
            DateFrom: "",
            DateTo: "",
        });
    };

    const columns = [
        { field: "id", headerName: "No", flex: 0.2, minWidth: 40 },
        { field: "ClientName", headerName: "Client Name", flex: 0.8, minWidth: 150 },
        { field: "Types", headerName: "Types", flex: 0.5, minWidth: 120 },
        { 
            field: "Response", 
            headerName: "Response", 
            flex: 3, 
            minWidth: 800, 
            wrap: true,
            renderCell: ({ row }) => <ResponseCell text={row.Response} limit={30} isAllExpanded={expandAll} />
        },
    ];

    const rows = useMemo(() => {
        return filterdata?.map((el, index) => ({
            id: index + 1,
            MainId: el._id,
            ClientName: el?.ClientID?.WhaClientName || el?.clientname || "-",
            Types: el?.Types || "-",
            Response: el?.Response || "-",
        })) || [];
    }, [filterdata]);

    const exportToExcel = () => {
        const sourceData = (filterdata && filterdata.length > 0) ? filterdata : (EInvoiceEwayResponseData || []);
        if (!sourceData || sourceData.length === 0) {
            alert("No response data available to export!");
            return;
        }

        setLoading(true);

        const dataToExport = sourceData.map((item, index) => ({
            "No": index + 1,
            "Client Name": item.ClientID?.WhaClientName || item.clientname || (typeof item.ClientID === "string" ? item.ClientID : "-"),
            "Types": item.Types || "-",
            "Response": typeof item.Response === "object" ? JSON.stringify(item.Response) : (item.Response || "-"),
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);

        // Auto column widths
        worksheet["!cols"] = Object.keys(dataToExport[0] || {}).map((key) => {
            const maxLen = Math.max(
                key.length,
                ...dataToExport.slice(0, 50).map((r) => String(r[key] || "").length)
            );
            return { wch: Math.min(Math.max(maxLen + 3, 10), 50) };
        });

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "EInvoice_Eway_Response");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });

        const data = new Blob([excelBuffer], {
            type: "application/octet-stream",
        });

        saveAs(data, "EInvoice_EWay_Response_Report.xlsx");

        setTimeout(() => {
            setLoading(false);
        }, 500);
    };

    if (EInvoiceEwayResponseLoading || !dataLoaded) {
        return <Loader />;
    }

    return (
        <div className="p-3">
            <div className="title flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3">
                <h3 className="text-xl font-bold main_title_page text-white px-5 py-2 rounded w-full lg:w-auto text-center lg:text-left">
                    E-Invoice & E-Way Response
                </h3>

                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full lg:w-auto items-center">
                    <div className="flex items-center space-x-2 bg-[#122A47] text-white px-4 py-2 rounded-lg shadow-md w-full sm:w-auto justify-center">
                        <span className="text-sm font-semibold">Expand All</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={expandAll}
                                onChange={(e) => setExpandAll(e.target.checked)}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                        </label>
                    </div>

                    <button
                        onClick={exportToExcel}
                        disabled={loading}
                        className="bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 disabled:opacity-50 flex items-center justify-center text-sm font-semibold"
                    >
                        <IoDocumentTextSharp size={18} className="me-1" />
                        {loading ? "Exporting..." : "Export to Excel"}
                    </button>

                    <button
                        onClick={() => setIsDrawerOpen(true)}
                        className="bg-gray-600 text-white px-3 py-2 rounded hover:bg-gray-700 flex items-center justify-center text-sm font-semibold"
                    >
                        <IoFilter size={18} className="me-1" />
                        Open Filters
                    </button>

                    <button
                        onClick={clearFilters}
                        className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 flex items-center justify-center text-sm font-semibold"
                    >
                        <LuLayoutGrid size={18} className="me-1" />
                        Show All
                    </button>
                </div>
            </div>

            <div className="rounded-lg overflow-hidden mt-5">
                {filterdata && filterdata.length > 0 ? (
                    <WpDynamicTable columns={columns} rows={rows} height="700px" rowsPerPage={10} />
                ) : (
                    <div className="text-center py-20 bg-gray-50">
                        <p className="text-gray-500 text-lg">No response data found.</p>
                    </div>
                )}
            </div>

            {isDrawerOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-2">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 flex flex-col">
                            <div className="flex items-center justify-between mb-6 border-b pb-4">
                                <h2 className="text-2xl font-bold text-gray-800">
                                    Filter Responses
                                </h2>
                                <button
                                    onClick={() => setIsDrawerOpen(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors text-3xl leading-none"
                                >
                                    &times;
                                </button>
                            </div>

                            <form className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div className="col-span-1 md:col-span-2">
                                    <label className="text-sm font-bold text-gray-700 mb-1 block">Client Name</label>
                                    <select
                                        name="ClientName"
                                        value={filters.ClientName}
                                        onChange={handleFilterChange}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                    >
                                        <option value="">All Clients</option>
                                        {[...new Set(EInvoiceEwayResponseData?.map(el => el?.ClientID?.WhaClientName))].filter(Boolean).map(client => (
                                            <option key={client} value={client}>{client}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="text-sm font-bold text-gray-700 mb-1 block">Response Type</label>
                                    <select
                                        name="Type"
                                        value={filters.Type}
                                        onChange={handleFilterChange}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                    >
                                        <option value="">All Types</option>
                                        <option value="Einvoice">E-Invoice</option>
                                        <option value="Eway">E-Way</option>
                                        <option value="GSTINSEARCH">GSTIN</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="text-sm font-bold text-gray-700 mb-1 block">From Date</label>
                                    <input
                                        type="date"
                                        name="DateFrom"
                                        value={filters.DateFrom}
                                        onChange={handleFilterChange}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="text-sm font-bold text-gray-700 mb-1 block">To Date</label>
                                    <input
                                        type="date"
                                        name="DateTo"
                                        value={filters.DateTo}
                                        onChange={handleFilterChange}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>
                            </form>

                            <div className="flex justify-end gap-3 mt-8 border-t pt-5">
                                <button
                                    type="button"
                                    onClick={clearFilters}
                                    className="px-6 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 font-semibold transition-colors"
                                >
                                    Clear Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EInvoice_Eway_Response;

